import type { TFunction } from 'i18next';
import type {
  CompletedTask,
  HmsNotification,
  HmsNotificationType,
  NotificationPreferences,
  SafetyRepTask,
  SafetyRepTeam,
} from '../../api/safetyRepWork';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';
import { Routes } from '../../enums/Routes';
import { buildRoute } from '../../utils/routeBuilder';
import {
  formatDay,
  fullDate,
  joinList,
  monthAndYear,
} from '../HRDashboard/hmsFormat';
import {
  currentStepIndex,
  dayAndMonth,
  PROCESS_STEPS,
} from '../WorkEnvironmentSurveyDetail/surveyProcessView';
import { surveyPath } from '../WorkEnvironmentSurveyDetail/surveyRoutes';

/*
 * What waits for the safety representative, where the teams are, and what they have done
 * before, is worked out in the API. What is here writes it out for the pages.
 */

const surveyRoute = (surveyId: number) => surveyPath('safetyRep', surveyId);

/** Signing and approving happen on the page of tasks. The rest is done on the page of the survey. */
export const taskLink = (task: SafetyRepTask) =>
  task.kind === 'sign' || task.surveyId === null
    ? Routes.SafetyRepSigning
    : surveyRoute(task.surveyId);

export const taskKey = (task: SafetyRepTask) =>
  [
    task.kind,
    task.surveyId ?? `${task.version?.subject}-${task.version?.id}`,
    ...task.areas,
  ].join('-');

/** Documentation and versions of the setup are answered on the card itself */
export const answeredOnCard = (task: SafetyRepTask) =>
  task.kind === 'sign' || task.kind === 'approveVersion';

export const TASK_COLORS: Record<
  SafetyRepTask['kind'],
  'accent' | 'info' | 'warning' | 'danger'
> = {
  sign: 'accent',
  approveVersion: 'accent',
  reviewPlan: 'info',
  highRisk: 'danger',
  assessRisk: 'warning',
};

const capitalized = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

/** «Før 15. oktober» when HR has set a deadline, otherwise since when it has waited */
const taskMeta = (task: SafetyRepTask, t: TFunction, language: string) => {
  const team = task.team ?? t('safetyRep.work.notifications.hr');
  return task.due
    ? t('safetyRep.work.tasks.before', {
        team,
        date: dayAndMonth(task.due, language),
      })
    : t(`safetyRep.work.tasks.${task.kind}.meta`, {
        team,
        date: formatDay(task.since, t, language),
      });
};

export const taskTexts = (
  task: SafetyRepTask,
  t: TFunction,
  language: string,
) => {
  const areas = task.areas.map((area) => area.toLowerCase());
  return {
    kind: t(`safetyRep.work.tasks.${task.kind}.kind`),
    meta: taskMeta(task, t, language),
    title: t(`safetyRep.work.tasks.${task.kind}.title`, {
      area: joinList(areas, t, 'and'),
      count: areas.length,
      subject: task.version
        ? t(`safetyRep.subjects.${task.version.subject}`, {
            version: task.version.version,
          }).toLowerCase()
        : '',
    }),
    text: task.version
      ? t(`safetyRep.signing.text.${task.version.subject}`)
      : t(`safetyRep.work.tasks.${task.kind}.text`, {
          areas: capitalized(joinList(areas, t, 'and')),
        }),
    action: t(`safetyRep.work.tasks.${task.kind}.action`),
  };
};

const SIGNING_TYPES: HmsNotificationType[] = [
  'DOCUMENTATION_TO_SIGN',
  'VERSION_TO_APPROVE',
  'VERSION_APPROVED_BY_HR',
  'DOCUMENTATION_DEVIATION',
];

export const notificationLink = (notification: HmsNotification) => {
  // News about their own role leads to the teams they represent
  if (notification.type === 'SAFETY_REP_ROLE') return Routes.SafetyRepTeams;
  return SIGNING_TYPES.includes(notification.type) || !notification.survey
    ? Routes.SafetyRepSigning
    : surveyRoute(notification.survey.id);
};

/** An evaluation starts the same survey again, and says so, as the email does */
export const notificationText = (notification: HmsNotification, t: TFunction) =>
  t(
    notification.aboutEvaluation
      ? 'safetyRep.work.notifications.evaluationStarted'
      : `safetyRep.work.notifications.types.${notification.type}`,
    { team: notification.survey?.team.name ?? '' },
  );

export const isUnread = (notification: HmsNotification) =>
  notification.readAt === null;

const waits = (notification: HmsNotification) =>
  isUnread(notification) && notification.kind !== 'info';

/**
 * What waits for the safety representative first, a serious report before a task, then what only
 * tells what happened, and what they have read. Each newest first, as the API sorts them.
 */
export const groupNotifications = (notifications: HmsNotification[]) => ({
  waiting: notifications
    .filter(waits)
    .sort(
      (a, b) =>
        Number(b.kind === 'serious') - Number(a.kind === 'serious'),
    ),
  info: notifications.filter(
    (notification) => isUnread(notification) && notification.kind === 'info',
  ),
  read: notifications.filter((notification) => !isUnread(notification)),
});

/** «Signer dokumentasjonen» for what waits, «Åpne» for the rest */
export const notificationAction = (notification: HmsNotification, t: TFunction) =>
  waits(notification)
    ? t(`safetyRep.work.notifications.actions.${notification.type}`, {
        defaultValue: t('safetyRep.work.notifications.open'),
      })
    : t('safetyRep.work.notifications.open');

/** Where the team is in the nine steps, or null before its first survey */
export const teamStep = (team: SafetyRepTeam) => {
  if (!team.survey) return null;
  const archived = team.survey.status === WorkEnvironmentSurveyStatus.ARCHIVED;
  return {
    index: archived
      ? PROCESS_STEPS.length
      : currentStepIndex(team.survey.status),
    archived,
  };
};

export const stageText = (team: SafetyRepTeam, t: TFunction) => {
  const step = teamStep(team);
  if (!step) return t('safetyRep.work.teams.notStarted');
  if (step.archived) return t('safetyRep.work.teams.archived');
  return t(`workEnvironmentProcess.steps.${PROCESS_STEPS[step.index]}.title`);
};

export const stageColor = (team: SafetyRepTeam) => {
  const step = teamStep(team);
  return !step ? 'neutral' : step.archived ? 'success' : 'info';
};

export const stepOfText = (team: SafetyRepTeam, t: TFunction) => {
  const step = teamStep(team);
  return step
    ? t('workEnvironmentProcess.stepOf', {
        current: Math.min(step.index + 1, PROCESS_STEPS.length),
        total: PROCESS_STEPS.length,
      })
    : t('safetyRep.work.teams.notStarted');
};

export type SegmentState = 'done' | 'current' | 'notStarted';

/** The nine steps as a bar, for a quick look at how far the team has come */
export const stepSegments = (team: SafetyRepTeam): SegmentState[] => {
  const step = teamStep(team);
  return PROCESS_STEPS.map((_, index) =>
    !step
      ? 'notStarted'
      : index < step.index
        ? 'done'
        : index === step.index
          ? 'current'
          : 'notStarted',
  );
};

/** The tag on the team, only when there is high risk */
export const highRiskTag = (team: SafetyRepTeam, t: TFunction) =>
  team.survey?.highRisk
    ? t('safetyRep.work.teams.highRiskTag', { count: team.survey.highRisk })
    : null;

/** For the table: unknown until the risk is calculated */
export const highRiskText = (team: SafetyRepTeam, t: TFunction) =>
  !team.survey || team.survey.highRisk === null
    ? '–'
    : t('safetyRep.work.teams.highRisk', { count: team.survey.highRisk });

const taskOf = (team: SafetyRepTeam, tasks: SafetyRepTask[]) =>
  tasks.find((candidate) => candidate.teamId === team.id);

const collectingText = (
  collecting: NonNullable<NonNullable<SafetyRepTeam['survey']>['collecting']>,
  t: TFunction,
  language: string,
) =>
  collecting.deadline
    ? t('safetyRep.work.teams.collecting', {
        ...collecting,
        date: dayAndMonth(collecting.deadline, language),
      })
    : t('safetyRep.work.teams.collectingNoDeadline', collecting);

/** A team the user leads is followed up as its team leader: the main safety representative, or no one, acts for it */
export const teamPathFor = (team: SafetyRepTeam, surveyId: number) =>
  surveyPath(team.leads ? 'team' : 'safetyRep', surveyId);

/** What happens next for the team: what waits for the safety representative first */
export const nextText = (
  team: SafetyRepTeam,
  tasks: SafetyRepTask[],
  t: TFunction,
  language: string,
) => {
  if (team.leads) return t('safetyRep.work.teams.leads');
  const task = taskOf(team, tasks);
  if (task) return taskTexts(task, t, language).title;
  const step = teamStep(team);
  if (!team.survey || !step) return t('safetyRep.work.teams.noSurvey');
  if (team.survey.collecting)
    return collectingText(team.survey.collecting, t, language);
  if (step.archived)
    return t('safetyRep.work.teams.archivedNext', {
      date: monthAndYear(team.survey.createdAt, language),
    });
  return t('safetyRep.work.teams.working');
};

/** The same, short enough for the table */
export const nextShort = (
  team: SafetyRepTeam,
  tasks: SafetyRepTask[],
  t: TFunction,
  language: string,
) => {
  if (team.leads) return t('safetyRep.work.teams.leadsShort');
  const task = taskOf(team, tasks);
  if (task) return t(`safetyRep.work.tasks.${task.kind}.short`);
  const deadline = team.survey?.collecting?.deadline;
  if (deadline)
    return t('safetyRep.work.teams.deadline', {
      date: dayAndMonth(deadline, language),
    });
  const step = teamStep(team);
  return !step || step.archived
    ? t('safetyRep.work.teams.nonePlanned')
    : t('safetyRep.work.teams.workingShort');
};

export const lastSurveyText = (
  team: SafetyRepTeam,
  t: TFunction,
  language: string,
) => {
  if (!team.survey) return '–';
  return team.survey.collecting
    ? t('safetyRep.work.teams.ongoing')
    : fullDate(team.survey.createdAt, language);
};

export const togglePreference = (
  preferences: NotificationPreferences,
  type: HmsNotificationType,
): NotificationPreferences => ({
  ...preferences,
  types: preferences.types.map((choice) =>
    choice.type === type ? { ...choice, enabled: !choice.enabled } : choice,
  ),
});

export const teamLeaders = (team: SafetyRepTeam, t: TFunction) =>
  team.leaders.length > 0
    ? team.leaders.join(', ')
    : t('safetyRep.work.teams.noLeader');

export const completedRoute = (task: CompletedTask) =>
  buildRoute(Routes.SafetyRepCompleted, { taskKey: task.key });

export const completedTitle = (task: CompletedTask, t: TFunction) =>
  t(`safetyRep.completed.titles.${task.kind}`, {
    team: task.team ?? '',
    area: joinList(
      task.areas.map((area) => area.toLowerCase()),
      t,
      'and',
    ),
    version: task.version ?? '',
  });

/** «Signert 5. april 2027» */
export const completedWhen = (
  task: CompletedTask,
  t: TFunction,
  language: string,
) =>
  t(`safetyRep.completed.when.${task.outcome}`, {
    date: fullDate(task.at, language),
  });

export const OUTCOME_COLORS: Record<
  CompletedTask['outcome'],
  'success' | 'warning' | 'info'
> = {
  signed: 'success',
  approved: 'success',
  returned: 'warning',
  commented: 'info',
};

const fromText = (task: CompletedTask, t: TFunction) =>
  task.team ?? t('safetyRep.work.notifications.hr');

/** The short facts on the page of one completed task */
export const completedFacts = (
  task: CompletedTask,
  t: TFunction,
  language: string,
) => [
  { key: 'task', value: t(`safetyRep.completed.tasks.${task.kind}`) },
  { key: 'from', value: fromText(task, t) },
  { key: 'outcome', value: completedWhen(task, t, language) },
  { key: 'by', value: t('safetyRep.completed.you') },
];

/** Where the task can be read again. Versions of the setup belong to HR. */
export const completedLink = (task: CompletedTask) =>
  task.surveyId === null ? null : surveyRoute(task.surveyId);

/** What happened, oldest first. The last event is where it ended. */
export const timeline = (task: CompletedTask, t: TFunction, language: string) =>
  task.events.map((event, index) => ({
    key: `${event.kind}-${event.at}`,
    text: t(`safetyRep.completed.events.${task.kind}.${event.kind}`, {
      team: task.team ?? '',
      version: task.version ?? '',
    }),
    when: formatDay(event.at, t, language),
    last: index === task.events.length - 1,
  }));
